import { ColumnDef, createColumnHelper } from '@tanstack/react-table'

import { usePreferences } from 'context/preferences/PreferencesContext'
import dayjs from 'dayjs'
import React, { useMemo } from 'react'

import { useCompanyUrl } from 'utils/useCompanyUrl'
import { IContactCompany } from '../../../models/contact_companies'

import { LinkCell } from '../CommonTableCells/LinkCell'
import { TruncatedLinkCell } from '../CommonTableCells/TruncatedLinkCell'
import { useFeatureFlagService } from '../../../utils/useFeatureFlagService'
import { DataTableColumnSort } from '../../DataTable/DataTableSorting/DataTableSortingStore'
import { getCommonContactCompanyColumns } from 'components/DataExpansionComponents/Common/CommonTable/CommonColumns'
import { createTableColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { commonSortOptionsHelper } from 'components/DataExpansionComponents/Common/commonSortOptionsHelper'
import { COMMON_COLUMNS } from 'components/DataExpansionComponents/Common/CommonColumnsInfo'
import { COMPANIES_COLUMNS } from './companiesColumnInfo'
import { PhoneCell } from '../CommonTableCells/PhoneCell'
import { ActionButtons } from 'components/Buttons/ActionButtons'

export const sortableFields = [
  'name',
  'created',
  'note_count',
  'deal_count',
  'contact_count',
  'sales_stages',
  'state',
  'city',
  'country',
] as const

export const sortableFieldOptions = commonSortOptionsHelper(
  sortableFields,
  [COMPANIES_COLUMNS, COMMON_COLUMNS],
  {
    sales_stages: 'Sales Stage',
  }
)

export const useCompaniesTableCols = ({
  sorting,
  inCampaigns,
  onDelete,
  onEdit,
}: {
  sorting?: DataTableColumnSort[]
  inCampaigns?: boolean
  onDelete?: (companyId: number) => void
  onEdit?: (company: IContactCompany) => void
}) => {
  const getUrl = useCompanyUrl()
  const featureFlag = useFeatureFlagService()
  const { preferences } = usePreferences()
  const tooltips: Record<string, unknown> = preferences?.tooltips || {}
  const columnHelper = createColumnHelper<IContactCompany>()
  const companyColumnHelper = createTableColumnHelper({
    columns: COMPANIES_COLUMNS,
    tooltips,
  })

  let columns: ColumnDef<IContactCompany, any>[] = useMemo(
    () => [
      columnHelper.accessor('name', {
        ...companyColumnHelper.retrieveColumn('name', {
          meta: {
            isEditable: false,
            isFirstColumn: true,
          },
        }),
        size: 250,
        header: () => null,
        cell: (info) => (
          <TruncatedLinkCell
            text={info.row.original.name}
            to={getUrl(info.row.original)}
          />
        ),
      }),
      columnHelper.accessor('created', {
        ...companyColumnHelper.retrieveColumn('created'),
        size: 150,
        cell: (info) => dayjs(info.getValue()).format('MM/DD/YYYY'),
      }),
      columnHelper.accessor('google_place_url', {
        ...companyColumnHelper.retrieveColumn('google_place_url'),
        size: 150,
        cell: (info) => (info.getValue() ? <LinkCell info={info} /> : '-'),
      }),
      columnHelper.accessor('city', {
        ...companyColumnHelper.retrieveColumn('city'),
      }),
      columnHelper.accessor('state', {
        ...companyColumnHelper.retrieveColumn('state'),
        cell: (info) => {
          return info.getValue()
        },
      }),
      columnHelper.accessor('phone_number', {
        ...companyColumnHelper.retrieveColumn('phone_number'),
        cell: (info) => {
          const phone = info.getValue()
          const extension = info.row.original.extension
          return <PhoneCell phoneNumber={phone} extension={extension} />
        },
      }),
      columnHelper.accessor('country', {
        ...companyColumnHelper.retrieveColumn('country'),
      }),
      ...getCommonContactCompanyColumns(
        columnHelper,
        sorting,
        preferences,
        inCampaigns
      ),
      ...(onDelete || onEdit
        ? [
            {
              header: 'Actions',
              size: 100,
              meta: {
                isEditable: false,
                isLastColumn: true,
              },
              cell: (info: any) => {
                const company = info.row.original as IContactCompany
                return (
                  <ActionButtons
                    className={'justify-start'}
                    onDelete={onDelete ? () => onDelete(company.id) : undefined}
                    onEdit={onEdit ? () => onEdit(company) : undefined}
                  />
                )
              },
            } as ColumnDef<IContactCompany, any>,
          ]
        : []),
    ],
    [tooltips, sorting, preferences, inCampaigns, onDelete, onEdit]
  )

  if (!featureFlag.enableCampaigns) {
    columns = columns.filter((col) => !col.id?.includes('campaigns'))
  }

  return columns
}
